import {COMPANY_ID} from '@constants/auth.constant'
import {useAuth} from '@context/Auth'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {ENV} from 'config/ENV.config'
import {createContext, useContext, useEffect, useMemo} from 'react'

/**
 * Per-company branding, delivered post-login via the portal verify-token payload
 * (each userInfo.companies[*] carries logoUrl + themeColor). We resolve the ACTIVE
 * company (the `cid` in storage) and:
 *   - retint the app by overriding Metronic's `--kt-primary*` CSS variables at runtime
 *     (same mechanism the dark/light toggle uses — inline styles on <html> beat both the
 *     `:root` and `[data-theme="dark"]` stylesheet rules, so one override covers both modes)
 *   - expose {logoUrl, companyName} for the header/sidebar to render.
 * Everything falls back to the shipped ErpBoi defaults when a company has no branding.
 */

type BrandingContextType = {
  logoUrl: string
  companyName: string
}

const BrandingContext = createContext<BrandingContextType>({logoUrl: '', companyName: ''})

export const useBranding = () => useContext(BrandingContext)

const KT_PRIMARY_VARS = [
  '--kt-primary',
  '--kt-primary-active',
  '--kt-primary-inverse',
  '--kt-primary-light',
  '--kt-primary-rgb',
]

const hexToRgb = (hex: string): [number, number, number] | null => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim())
  if (!m) return null
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
}

const toHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('')

// Mix a channel toward black by `amt` (0..1) for the darker "active" state.
const darkenChannel = (c: number, amt: number) => Math.round(c * (1 - amt))

const applyThemeColor = (hex?: string) => {
  const root = document.documentElement
  const rgb = hex ? hexToRgb(hex) : null
  if (!hex || !rgb) {
    KT_PRIMARY_VARS.forEach((v) => root.style.removeProperty(v))
    return
  }
  const [r, g, b] = rgb
  root.style.setProperty('--kt-primary', hex)
  root.style.setProperty('--kt-primary-rgb', `${r}, ${g}, ${b}`)
  root.style.setProperty(
    '--kt-primary-active',
    toHex(darkenChannel(r, 0.1), darkenChannel(g, 0.1), darkenChannel(b, 0.1))
  )
  root.style.setProperty('--kt-primary-inverse', '#ffffff')
  // translucent tint — reads acceptably in both light and dark mode
  root.style.setProperty('--kt-primary-light', `rgba(${r}, ${g}, ${b}, 0.1)`)
}

// Sidebar colours are a separate, company-configured pair (asideColor bg + asideTextColor text) —
// NOT derived from the accent. They are set as the --app-sidebar-* custom properties that
// layout/sidebar/_sidebar-dark.scss reads (its fallbacks are the template's dark sidebar), so
// hover/active/heading states all follow without selector overrides.
//
// Every state is built from the company's OWN pair, never from the accent (--kt-primary): a company
// commonly paints its sidebar in its brand colour, and accent-coloured text on it disappears (seen on
// stage: a #14a4de sidebar with the default #009EF7 accent hid every open group heading and every
// hovered item). Headings and hover keep the text colour; the active item is the inverse pill —
// text-coloured background, sidebar-coloured text — which contrasts with any pair that is readable.
const SIDEBAR_VARS = [
  '--app-sidebar-bg',
  '--app-sidebar-separator',
  '--app-sidebar-text',
  '--app-sidebar-icon',
  '--app-sidebar-muted',
  '--app-sidebar-heading',
  '--app-sidebar-hover',
  '--app-sidebar-hover-bg',
  '--app-sidebar-active-text',
  '--app-sidebar-active-bg',
  '--app-sidebar-btn-bg',
]

const applySidebar = (bg?: string, text?: string) => {
  const root = document.documentElement
  SIDEBAR_VARS.forEach((v) => root.style.removeProperty(v))
  if (!bg) return
  root.style.setProperty('--app-sidebar-bg', bg)
  // Without a text colour the template's light-on-dark defaults apply (white headings/hover, accent
  // active item) — correct for the dark sidebars companies usually pick.
  if (!text) return
  const rgb = hexToRgb(text)
  const tint = (alpha: number) => (rgb ? `rgba(${rgb.join(', ')}, ${alpha})` : text)
  root.style.setProperty('--app-sidebar-text', text)
  root.style.setProperty('--app-sidebar-icon', text)
  root.style.setProperty('--app-sidebar-heading', text)
  root.style.setProperty('--app-sidebar-hover', text)
  root.style.setProperty('--app-sidebar-hover-bg', tint(0.16))
  root.style.setProperty('--app-sidebar-muted', tint(0.6))
  root.style.setProperty('--app-sidebar-separator', tint(0.3))
  root.style.setProperty('--app-sidebar-btn-bg', tint(0.12))
  root.style.setProperty('--app-sidebar-active-bg', text)
  root.style.setProperty('--app-sidebar-active-text', bg)
}

export const BrandingProvider = ({children}: {children: React.ReactNode}) => {
  const {userInfo} = useAuth()

  const active = useMemo(() => {
    const companies = userInfo?.companies || []
    const cid = LocalStorageService.get(COMPANY_ID)
    return companies.find((c) => c.companyId === cid) || companies[0]
  }, [userInfo])

  const themeColor = active?.themeColor
  const asideColor = active?.asideColor
  const asideTextColor = active?.asideTextColor
  const logoUrl = active?.logoUrl ? `${ENV.portalGetway}/${active.logoUrl}` : ''
  const companyName = active?.companyName || ''

  useEffect(() => {
    applyThemeColor(themeColor)
    return () => applyThemeColor(undefined)
  }, [themeColor])

  useEffect(() => {
    applySidebar(asideColor, asideTextColor)
    return () => applySidebar(undefined, undefined)
  }, [asideColor, asideTextColor])

  return (
    <BrandingContext.Provider value={{logoUrl, companyName}}>{children}</BrandingContext.Provider>
  )
}
